<!-- MenuView.vue -->
<script setup lang="ts">
import { usePlanningStore } from '@/stores/planningStore';
import type { PlannedMenu } from '@/types/planning';
import Checkbox from 'primevue/checkbox';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

// Define props for the component
const props = defineProps<{
  date: Date;
  plannedMenu: PlannedMenu;
}>();

const router = useRouter();
const planningStore = ref(usePlanningStore());

const isSelected = computed(() => {
  return (
    planningStore.value.dishToPlan?.selectedPlannedMenuIds.includes(props.plannedMenu.id) ||
    planningStore.value.dishTitleToPlan?.selectedPlannedMenuIds.includes(props.plannedMenu.id) ||
    false
  );
});

const isDishPlanning = computed(() => {
  return planningStore.value.dishToPlan !== null || planningStore.value.dishTitleToPlan !== null;
});
// Define a list of dishes for the current day

// Add a new dish to the list

function goToMenuDetails() {
  // if (planningStore.value.dishToPlanClipboard) {
  //   // You're in the modal, so display the menu details in the sidebar
  //   planningStore.value.selectedPlannedMenu = props.plannedMenu;
  //   // Trigger any sidebar opening logic if needed
  // } else {
  // Not in modal mode, proceed with the usual navigation
  router.push(`/planning/planned-menu/${props.plannedMenu.id}`);
  // }
}

const sortedAndFilledCourses = computed(() => {
  const courseTypes = [1, 2, 3]; // Represents Entrée, Plat principal, Dessert
  const courses = props.plannedMenu.menu_courses.slice().sort((a, b) => a.order - b.order);

  // Create a new array that includes all three types, filling in missing types with placeholders
  return courseTypes.map((type) => {
    const foundCourse = courses.find((course) => course.order === type);
    return foundCourse ? foundCourse : { id: `missing-${type}`, name: '', dish: { name: '***' }, order: type };
  });
});

function handleClickPlannedMenu() {
  if (!props.plannedMenu.tempId) {
    if (planningStore.value.dishToPlan) {
      const plannedMenuId = props.plannedMenu.id;
      const selectedIds = planningStore.value.dishToPlan.selectedPlannedMenuIds;

      if (selectedIds.includes(plannedMenuId)) {
        // Remove the ID if it's already selected
        planningStore.value.dishToPlan.selectedPlannedMenuIds = selectedIds.filter((id) => id !== plannedMenuId);
      } else {
        // Add the ID if it's not yet selected
        planningStore.value.dishToPlan.selectedPlannedMenuIds.push(plannedMenuId);
      }
    } else if (planningStore.value.dishTitleToPlan) {
      const plannedMenuId = props.plannedMenu.id;
      const selectedIds = planningStore.value.dishTitleToPlan.selectedPlannedMenuIds;

      if (selectedIds.includes(plannedMenuId)) {
        // Remove the ID if it's already selected
        planningStore.value.dishTitleToPlan.selectedPlannedMenuIds = selectedIds.filter((id) => id !== plannedMenuId);
      } else {
        // Add the ID if it's not yet selected
        planningStore.value.dishTitleToPlan.selectedPlannedMenuIds.push(plannedMenuId);
      }
    } else {
      // If no dish is being planned, navigate to menu details
      goToMenuDetails();
    }
  }
}

const checkboxPt = {
  root: { class: 'custom-checkbox' },
  // input: { class: 'custom-checkbox-input' },
  box: { style: 'width: 1.5rem; height: 1.5rem; border: 2px solid gray' },
  icon: { style: 'font-size: 1rem;' },
};
</script>

<template>
  <!-- Right side for the card -->
  <div
    v-if="props.plannedMenu.tempId"
    class="relative mx-2 my-4 flex h-56 w-52 flex-grow items-start rounded-xl bg-gray-200 px-4 py-8 opacity-50 shadow-clickable-card"
  >
    <div
      class="absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 overflow-hidden truncate text-ellipsis whitespace-nowrap rounded-md bg-gray-300 px-4 py-2 text-center font-bold text-xs text-black"
    >
      <p class="h-4 w-24"></p>
    </div>
    <div class="flex flex-col justify-around gap-3 text-sm"></div>
  </div>
  <div v-if="!props.plannedMenu.tempId" class="relative">
    <div
      class="relative mx-2 my-4 flex h-56 w-52 flex-grow cursor-pointer items-start rounded-xl border-[1.5px] border-primary-500 bg-primary-100 px-4 py-8 shadow-clickable-card"
      :class="{
        'opacity-60': !isSelected && isDishPlanning,
      }"
      @click="handleClickPlannedMenu"
    >
      <div
        class="absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 overflow-hidden truncate text-ellipsis whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-center font-bold text-xs text-white"
      >
        {{ props.plannedMenu.name }}
      </div>
      <div class="flex flex-col justify-around gap-3 text-sm">
        <ul class="flex list-none flex-col">
          <li v-for="menuCourse in sortedAndFilledCourses" :key="menuCourse.id" class="relative w-44">
            <div
              :class="{
                'justify-center': menuCourse.dish?.name === '***' || menuCourse?.name === '***',
              }"
              class="flex h-12 items-center text-ellipsis"
            >
              <p class="line-clamp-3 leading-tight">
                {{ menuCourse.dish?.name ?? menuCourse.name }}
              </p>
            </div>
            <div v-if="menuCourse.order !== 3" class="mx-auto my-2 h-0.5 w-6 rounded-full bg-primary-500"></div>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="isDishPlanning" class="absolute right-2 top-2">
      <Checkbox :binary="true" :model-value="isSelected" class="m-2" :pt="checkboxPt" @click="handleClickPlannedMenu" />
    </div>
  </div>
</template>

<style scoped></style>
